// Copyright 2018 Sorama B.V.

import { bindable, computedFrom, containerless } from "aurelia-framework";
import { DrawLayer, Margin, Pair, Rect, Size } from "controls/plot/IPlotPart";
import { PlotPartBase } from "controls/plot/PlotPartBase";
import { Quad } from "datatypes/Generic/Quad";
import { IExportable } from "libraries/Export/IExportable";
import { AffineTransformFinder } from "libraries/Math/AffineTransformFinder";
import { Matrix } from "libraries/Math/Matrix";
import { SVGNode } from "libraries/SVG/SVGNode";
import { Unit } from "units/Unit";
import { IPlotParent } from "../IPlotParent";
import { DrawImageProgram } from "../programs/DrawImageProgram";

@containerless
export class BackgroundImagePart extends PlotPartBase implements IExportable {
    private readonly imageElement: HTMLImageElement;
    private drawProgram: DrawImageProgram;

    //#region fields
    @bindable
    public src: string;

    @bindable({ defaultValue: Quad.unitQuad })
    public regionQuad: Quad;

    private _srcTransform: number[][];
    @computedFrom("_srcTransform")
    public get srcTransform(): string {
        return AffineTransformFinder.toCSSTransform(this._srcTransform);
    }

    @computedFrom("actualSize")
    public get safeSize(): Size {
        return this.actualSize && this.actualSize.width > 0 && this.actualSize.height > 0
            ? this.actualSize
            : { width: 1, height: 1 };
    }

    private zoomFactorX: number;
    private zoomFactorY: number;
    private xZoomOffset: number;
    private yZoomOffset: number;
    private translateX: number;
    private translateY: number;

    private cachedRender: string;
    private cachedRenderWidth: number;
    private cachedRenderHeight: number;

    private initialPictureLoad = true;

    //#region Constructor and Initialisation
    constructor() {
        super(0, DrawLayer.Background);
    }

    public bind(context: IPlotParent): void {
        super.bind(context);
    }

    public attached(): void {
        super.attached();
        if (!this.imageElement) {
            throw new Error("Invalid view");
        }

        this.imageElement.style.opacity = "0";
        this.imageElement.addEventListener("load", this.imageLoadComplete);
    }

    public detached(): void {
        super.detached();
        this.imageElement.removeEventListener("load", this.imageLoadComplete);
    }

    public onMeasureMargin(): Margin {
        // No extra margin required
        return new Margin(0, 0, 0, 0);
    }

    public onSize(size: Size, margin: Margin): void {
        super.onSize(size, margin);

        // Wait before setting the size until we get a valid size
        this.size = size;
        this.margin = margin;

        this.zoomFactorX = 1;
        this.zoomFactorY = 1;

        this.translateX = 0;
        this.translateY = 0;

        // Size changes invalidate rendered image
        this.cachedRender = undefined;
    }

    public onViewPortComputed(viewPort: Rect, viewPortBounds: Rect, units: Pair<Unit>): void {
        super.onViewPortComputed(viewPort, viewPortBounds, units);

        if (!this.actualSize) return;

        this.zoomFactorX = this.viewPortBounds.width / this.viewPort.width;
        this.zoomFactorY = this.viewPortBounds.height / this.viewPort.height;

        const xOffset = this.viewPort.x - this.viewPortBounds.x;
        this.xZoomOffset = (this.viewPortBounds.width - this.viewPortBounds.width / this.zoomFactorX) / 2;
        this.translateX = ((this.xZoomOffset - xOffset) / this.viewPortBounds.width) * this.actualSize.width;

        const yOffset = this.viewPort.y - this.viewPortBounds.y;
        this.yZoomOffset = (this.viewPortBounds.height - this.viewPortBounds.height / this.zoomFactorY) / 2;
        this.translateY = ((yOffset - this.yZoomOffset) / this.viewPortBounds.height) * this.actualSize.height;
        this.cachedRender = undefined;

        this.updateImageRegion();
    }

    //#endregion

    //#region Change Handlers

    public regionQuadChanged(): void {
        // Region changes invalidate rendered image
        this.cachedRender = undefined;
        this.updateImageRegion();
    }

    public srcChanged(): void {
        if (!this.src) {
            // Set opacity of image to zero and set flag to set opacity back to 1 when picture has been loaded. (this prevents box that source is invalid from being shown)
            this.imageElement.style.opacity = "0";
            this.initialPictureLoad = true;
        }

        // source changes invalidate rendered image
        this.cachedRender = undefined;
    }

    private imageLoadComplete = (): void => {
        if (this.initialPictureLoad) {
            // Set opacity to 1 and unset flag.
            this.imageElement.style.opacity = "1";
            this.initialPictureLoad = false;
        }

        this.updateImageRegion();
    };

    //#endregion

    //#region Update methods

    private updateImageRegion(): void {
        if (!this.regionQuad || Quad.equals(this.regionQuad, Quad.unitQuad)) {
            // Use the original source
            this._srcTransform = undefined;
            return;
        }

        // Find affine transform from the region quad to the unit quad
        this._srcTransform = AffineTransformFinder.findTransform4(this.regionQuad, Quad.unitQuad);
    }

    //#endregion

    //#region IExportable
    public exportToSvg(suggestedWidth?: number, suggestedHeight?: number): Promise<SVGNode> {
        if (!this.imageElement) return undefined;

        // Adjust the suggested width/height to fit our aspect ratio
        if (suggestedWidth && suggestedHeight) {
            const scale = (suggestedWidth / this.actualSize.width + suggestedHeight / this.actualSize.height) / 2;
            suggestedWidth = Math.round(scale * this.actualSize.width);
            suggestedHeight = Math.round(scale * this.actualSize.height);
        }

        let imgPromise: Promise<string>;
        if (!this.src) {
            imgPromise = Promise.resolve(undefined);
        } else if (
            this._srcTransform ||
            this.zoomFactorX !== 1 ||
            this.zoomFactorY !== 1 ||
            this.translateX !== 0 ||
            this.translateY !== 0
        ) {
            if (
                this.cachedRender &&
                ((!suggestedWidth && !suggestedHeight) ||
                    (Math.abs(this.cachedRenderWidth - suggestedWidth) < 1 &&
                        Math.abs(this.cachedRenderHeight - suggestedHeight) < 1))
            ) {
                imgPromise = Promise.resolve(this.cachedRender);
            } else {
                const width = suggestedWidth && suggestedHeight ? suggestedWidth : this.actualSize.width;
                const height = suggestedHeight && suggestedWidth ? suggestedHeight : this.actualSize.height;

                // Render image using webgl
                if (!this.drawProgram) {
                    // Create a draw program
                    this.drawProgram = new DrawImageProgram(
                        document.createElement("canvas"),
                        document.createElement("canvas"),
                        width,
                        height
                    );
                } else {
                    // Make sure the size is up to date
                    this.drawProgram.reset(width, height);
                }

                if (this._srcTransform) {
                    // Apply source transform if applicable
                    this.drawProgram.pushMatrix(this._srcTransform);
                }

                // Apply translation and zoom matrices
                imgPromise = this.drawProgram
                    .pushMatrix(
                        Matrix.translate(
                            this.translateX / this.actualSize.width - 0.5,
                            this.translateY / this.actualSize.height - 0.5,
                            0
                        )
                    ) // Translate the center to (0,0) and apply offset
                    .pushMatrix(Matrix.scale(this.zoomFactorX, this.zoomFactorY, 1, 1)) // Apply zoom
                    .pushMatrix(Matrix.translate(0.5, 0.5, 0)) // Go back to (0.5,0.5)
                    .drawImage(this.src)
                    .then((img) => {
                        // Cache this image and return
                        this.cachedRenderWidth = width;
                        this.cachedRenderHeight = height;
                        return (this.cachedRender = img);
                    });
            }
        } else {
            imgPromise = Promise.resolve(this.src);
        }

        return imgPromise.then((dataUrl) => {
            const svgTopNode = new SVGNode("svg");

            const clipId = "backgroundpartclip" + new Date().getTime();
            const clipPathNode = new SVGNode("clipPath");
            const clipRectNode = new SVGNode("rect");
            clipRectNode.attributes["x"] = "0";
            clipRectNode.attributes["y"] = "0";
            clipRectNode.attributes["width"] = this.actualSize.width + "px";
            clipRectNode.attributes["height"] = this.actualSize.height + "px";
            clipPathNode.addChild(clipRectNode);
            clipPathNode.attributes["id"] = clipId;

            const svgGNode = new SVGNode("g");
            svgGNode.attributes["clip-path"] = "url(#" + clipId + ")";
            svgGNode.addChild(clipPathNode);

            if (dataUrl) {
                const svgImageNode = new SVGNode("image");
                svgImageNode.attributes["xlink:href"] = dataUrl;
                svgImageNode.attributes["x"] = "0";
                svgImageNode.attributes["y"] = "0";
                svgImageNode.attributes["width"] = this.actualSize.width + "px";
                svgImageNode.attributes["height"] = this.actualSize.height + "px";

                svgGNode.addChild(svgImageNode);
            } else {
                const blackRectNode = new SVGNode("rect");
                blackRectNode.attributes["x"] = "0";
                blackRectNode.attributes["y"] = "0";
                blackRectNode.attributes["width"] = this.actualSize.width + "px";
                blackRectNode.attributes["height"] = this.actualSize.height + "px";
                blackRectNode.attributes["fill"] = "black";
                blackRectNode.addCustomizableAttribute("Background Color", "fill", "color");
                blackRectNode.addCustomizableAttribute("Background Opacity", "opacity", "number");

                svgGNode.addChild(blackRectNode);
            }

            svgTopNode.addChild(svgGNode);

            return svgTopNode;
        });
    }
    //#endregion
}
